class MegaMenu {
    constructor(selector = ".mega-nav") {
        this.megaNav = document.querySelector(selector);
        if (!this.megaNav) return;

        this.triggers = this.megaNav.querySelectorAll("button[popovertarget='mega-menu-popover-container']");
        if (this.triggers.length === 0) return;

        this.popover = document.getElementById("mega-menu-popover-container");
        this.contentContainer = this.popover?.querySelector(".mega-menu-popover-content");
        if (!this.popover || !this.contentContainer) return;

        this.globalBackdrop = null;
        this.currentMenuId = null;

        this.initializeBackdrop();
        this.attachTriggerEvents();
        this.observeStickyStateChange();
        this.observeScroll();
    }

    observeStickyStateChange() {
        const headerElement = document.querySelector(".ms_flexible-rearrangeable-elements-header");
        if (!headerElement) return;

        // Watch for class changes on the header (e.g., sticky state changes)
        const observer = new MutationObserver(() => {
            // Recalculate gap if popover is open
            if (this.popover.matches(":popover-open")) {
                this.positionPopover();
            }
        });

        observer.observe(headerElement, {
            attributes: true,
            attributeFilter: ["class"],
        });
    }

    observeScroll() {
        let scrollTimeout;
        window.addEventListener("scroll", () => {
            clearTimeout(scrollTimeout);
            // Recalculate gap if popover is open, debounced to avoid excessive calculations
            scrollTimeout = setTimeout(() => {
                if (this.popover.matches(":popover-open")) {
                    this.positionPopover();
                }
            }, 50);
        });
    }

    initializeBackdrop() {
        this.globalBackdrop = document.getElementById("mega-menu-global-backdrop");
        if (!this.globalBackdrop) {
            this.globalBackdrop = document.createElement("div");
            this.globalBackdrop.id = "mega-menu-global-backdrop";
            this.globalBackdrop.className = "mega-menu-backdrop";
            document.body.appendChild(this.globalBackdrop);
        }
    }

    showBackdrop() {
        this.globalBackdrop.classList.add("active");
        document.body.classList.add("mega-menu-open");
    }

    hideBackdrop() {
        this.globalBackdrop.classList.remove("active");
        document.body.classList.remove("mega-menu-open");
    }

    getHoverDelays() {
        const computedStyle = getComputedStyle(document.documentElement);
        return {
            openDelay: parseInt(computedStyle.getPropertyValue("--mega-menu-hover-delay-open")) || 50,
            closeDelay: parseInt(computedStyle.getPropertyValue("--mega-menu-hover-delay-close")) || 200,
        };
    }

    getTransitionSettings(popover) {
        const computedStyle = getComputedStyle(popover);
        return {
            duration: computedStyle.getPropertyValue("--mega-menu-popover-transition-duration-enter") || "425ms",
            easing:
                computedStyle.getPropertyValue("--mega-menu-popover-transition-timing-enter") ||
                "cubic-bezier(.65,0,.35,1)",
        };
    }

    measureHeight(popover) {
        const currentMaxHeight = popover.style.maxHeight;
        popover.style.maxHeight = "none";
        const height = popover.scrollHeight;
        popover.style.maxHeight = currentMaxHeight;
        return height;
    }

    applyTransitionStyles(popover, { duration, easing }) {
        // Use shorthand to override CSS transitions
        popover.style.transition = `max-height ${duration} ${easing}`;
    }

    cleanupTransitionStyles(popover) {
        popover.style.transition = "";
        popover.style.transitionProperty = "";
        popover.style.transitionDuration = "";
        popover.style.transitionTimingFunction = "";
        popover.style.maxHeight = "";
    }

    animateOpen(popover) {
        const transitionSettings = this.getTransitionSettings(popover);

        // Measure height after a small delay to ensure content is laid out
        requestAnimationFrame(() => {
            const targetHeight = this.measureHeight(popover);

            popover.classList.add("animating-height");

            // Set initial collapsed state and apply transitions with !important
            popover.style.transition = "none";
            popover.style.setProperty("max-height", "0px", "important");
            // Force reflow to register the 0 state
            // eslint-disable-next-line no-unused-expressions
            popover.offsetHeight;

            this.applyTransitionStyles(popover, transitionSettings);
            // Force another reflow to ensure transition styles are applied
            // eslint-disable-next-line no-unused-expressions
            popover.offsetHeight;

            // Now animate to target height with !important
            popover.style.setProperty("max-height", targetHeight + "px", "important");

            const onAnimationEnd = () => {
                popover.removeEventListener("transitionend", onAnimationEnd);
                popover.style.maxHeight = "none";
                popover.classList.remove("animating-height");
                this.cleanupTransitionStyles(popover);
            };

            popover.addEventListener("transitionend", onAnimationEnd, { once: true });
        });
    }

    animateClose(popover, onComplete) {
        const transitionSettings = this.getTransitionSettings(popover);
        const currentHeight = popover.scrollHeight;

        popover.classList.add("animating-height");

        // Set initial height and apply transitions with !important
        popover.style.transition = "none";
        popover.style.setProperty("max-height", currentHeight + "px", "important");
        // Force reflow to register the current height
        // eslint-disable-next-line no-unused-expressions
        popover.offsetHeight;

        this.applyTransitionStyles(popover, transitionSettings);
        // Force another reflow to ensure transition styles are applied
        // eslint-disable-next-line no-unused-expressions
        popover.offsetHeight;

        // Now animate to 0 with !important
        popover.style.setProperty("max-height", "0px", "important");

        const onAnimationEnd = () => {
            popover.removeEventListener("transitionend", onAnimationEnd);
            popover.classList.remove("animating-height");
            this.cleanupTransitionStyles(popover);
            if (onComplete) onComplete();
        };

        popover.addEventListener("transitionend", onAnimationEnd, { once: true });
    }

    /**
     * Is this element part of the menu's own chrome?
     *
     * The popover hangs below the whole header, so the strip between the menu
     * buttons and the popover -- the rest of the row the buttons sit in, plus
     * every row under it -- belongs to neither. Moving between the two crosses
     * it, and without this that counted as leaving the menu: the popover closed
     * on the way to it, and again on the way back from it to another menu item.
     *
     * A null node means the pointer left the window, which does close.
     */
    withinMenuChrome(node) {
        if (! (node instanceof Node)) return false;

        if (this.popover.contains(node)) return true;

        const header = document.querySelector(".ms_flexible-rearrangeable-elements-header");

        return header ? header.contains(node) : false;
    }

    /**
     * Close the popover, backdrop first so both animate together.
     */
    closePopover() {
        if (!this.popover.matches(":popover-open")) return;

        this.hideBackdrop();
        this.animateClose(this.popover, () => {
            if (this.popover.matches(":popover-open")) {
                this.popover.hidePopover();
            }
        });
    }

    initializeTabs(container) {
        if (!container) return;

        const groups = container.querySelectorAll("el-tab-group");
        const panels = [];

        groups.forEach((tabs) => {
            const buttons = tabs.querySelectorAll("el-tab-list button");
            const contents = Array.from(tabs.querySelectorAll("el-tab-panels .tab-content"));
            if (!buttons.length) return;

            const setActive = (i) => {
                tabs.setActiveTab(i);
                buttons.forEach((b, bi) => b.classList.toggle("active", bi === i));
                contents.forEach((c, ci) => c.classList.toggle("active", ci === i));
            };

            buttons.forEach((btn, i) => {
                btn.addEventListener("mouseover", () => {
                    clearTimeout(btn.hoverTimeout);
                    btn.hoverTimeout = setTimeout(() => setActive(i), 50);
                });
                btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    setActive(i);
                    if (btn.dataset.href) {
                        if (btn.dataset.target && btn.dataset.target !== "_self") {
                            window.open(btn.dataset.href, btn.dataset.target);
                        } else {
                            window.location.href = btn.dataset.href;
                        }
                    }
                });
            });

            panels.push(...contents);
        });

        if (!panels.length) return;

        this.equalizePanels(container, panels);
    }

    /**
     * Equalize the tab panels of one menu.
     *
     * Equalizer measures what it can see, so every panel is shown for the
     * measurement and put back afterwards.
     *
     * Scoped to the container the caller already has: re-initialising Foundation
     * document-wide would restart every plugin on the page, the sticky header
     * this menu observes included.
     */
    equalizePanels(container, panels) {
        if (!window.jQuery || !window.jQuery.fn.foundation) return;

        const originalDisplay = panels.map((c) => c.style.display);
        const wasActive = panels.map((c) => c.classList.contains("active"));

        panels.forEach((c) => {
            c.style.display = "block";
            c.classList.add("active");
        });

        jQuery(container).foundation();

        panels.forEach((c, i) => {
            c.classList.toggle("active", wasActive[i]);
            c.style.display = originalDisplay[i];
        });
    }

    swapContent(menuId, trigger) {
        const contentElement = this.megaNav.querySelector(`.mega-menu-content[data-menu-id="${menuId}"]`);
        if (!contentElement) return;

        const isCurrentMenu = this.currentMenuId === menuId;
        if (isCurrentMenu && this.popover.matches(":popover-open")) {
            return; // Already showing this menu
        }

        // Fade out current content when switching menus
        if (this.popover.matches(":popover-open")) {
            this.contentContainer.style.opacity = "0";
            this.contentContainer.style.transition = "opacity 150ms ease-out";

            setTimeout(() => {
                this.applyContent(contentElement, menuId);

                // Remeasure height after content swap
                requestAnimationFrame(() => {
                    const newHeight = this.measureHeight(this.popover);
                    this.popover.style.setProperty("max-height", newHeight + "px", "important");
                });

                // Fade in new content
                this.contentContainer.style.opacity = "1";
                this.contentContainer.style.transition = "opacity 200ms ease-in";
            }, 150);
        } else {
            // First time opening - just set content
            this.applyContent(contentElement, menuId);

            this.contentContainer.style.opacity = "1";
            this.contentContainer.style.transition = "";
        }
    }

    /**
     * Move one menu's markup into the popover.
     *
     * Shared by both swap paths. What stays outside it is what genuinely
     * differs: the first open sets no transition and does not remeasure, a
     * switch fades and remeasures.
     */
    applyContent(contentElement, menuId) {
        this.contentContainer.innerHTML = contentElement.innerHTML;
        this.currentMenuId = menuId;

        const variant = contentElement.dataset.variant || "tabs";
        const width = contentElement.dataset.width || "normal";
        const contentWidth = contentElement.dataset.contentWidth || "normal";

        this.popover.className = `mega-menu-popover ${width} type-${variant}`;

        const container = this.contentContainer.querySelector(".container");
        if (container) {
            container.className = `container ${contentWidth}`;
        }

        this.initializeTabs(this.contentContainer);
    }

    positionPopover() {
        const headerElement = document.querySelector(".ms_flexible-rearrangeable-elements-header");

        if (headerElement) {
            // Get the first trigger (top row) to calculate gap from header
            const firstTrigger = this.triggers[0];
            const firstTriggerRect = firstTrigger.getBoundingClientRect();

            // Use the container div inside header for more accurate measurement
            const containerElement = headerElement.querySelector(".container");
            const containerRect = containerElement ? containerElement.getBoundingClientRect() : headerElement.getBoundingClientRect();

            // Account for admin bar margin-top on html element
            const htmlMarginTop = parseInt(getComputedStyle(document.documentElement).marginTop) || 0;

            // Calculate gap from first trigger and container bottom
            // Use Math.max to prevent negative values when sticky is stuck
            const gap = Math.max(0, containerRect.bottom - firstTriggerRect.bottom + htmlMarginTop);
            this.popover.style.setProperty("--anchor-gap", gap + "px", "important");
        }
    }

    attachTriggerEvents() {
        const delays = this.getHoverDelays();
        let pendingCloseTimeout = null;
        let isHoveringMenu = false;

        // Handle popover closing via any means (native popover, tab key, etc.)
        this.popover.addEventListener("toggle", (e) => {
            if (e.newState === "closed") {
                this.hideBackdrop();
            }
        });

        // Single popover mouseleave handler
        this.popover.addEventListener("mouseleave", (e) => {
            if (!isHoveringMenu && !this.withinMenuChrome(e.relatedTarget)) {
                pendingCloseTimeout = setTimeout(() => {
                    this.closePopover();
                }, delays.closeDelay);
            }
        });

        // Popover mouseenter - keep it open
        this.popover.addEventListener("mouseenter", () => {
            clearTimeout(pendingCloseTimeout);
        });

        // Leaving the header chrome sideways -- to the logo, the cart, and then off
        // the header entirely -- never touches the popover, so nothing above would
        // arm the close.
        const header = document.querySelector(".ms_flexible-rearrangeable-elements-header");

        header?.addEventListener("mouseleave", (e) => {
            if (this.withinMenuChrome(e.relatedTarget)) {
                return;
            }

            clearTimeout(pendingCloseTimeout);
            pendingCloseTimeout = setTimeout(() => {
                this.closePopover();
            }, delays.closeDelay);
        });

        header?.addEventListener("mouseenter", () => {
            clearTimeout(pendingCloseTimeout);
        });

        this.triggers.forEach((trigger) => {
            const li = trigger.parentElement;
            // Get menu ID from the content div in the same parent li
            const contentDiv = li.querySelector(".mega-menu-content");
            const menuId = contentDiv ? parseInt(contentDiv.getAttribute("data-menu-id")) : null;

            // Skip dropdown items (they don't have a mega-menu-content div)
            if (menuId === null) return;

            // Handle click navigation on trigger button
            trigger.addEventListener("click", (e) => {
                if (trigger.dataset.href) {
                    e.preventDefault();
                    if (trigger.dataset.target && trigger.dataset.target !== "_self") {
                        window.open(trigger.dataset.href, trigger.dataset.target);
                    } else {
                        window.location.href = trigger.dataset.href;
                    }
                }
            });

            // Open on hover
            li.addEventListener("mouseenter", () => {
                isHoveringMenu = true;
                clearTimeout(li.openTimeout);
                clearTimeout(pendingCloseTimeout);

                li.openTimeout = setTimeout(() => {
                    const isFirstOpen = !this.popover.matches(":popover-open");
                    this.swapContent(menuId, trigger);
                    this.positionPopover();
                    this.popover.showPopover();
                    this.showBackdrop();

                    // Animate open on first open
                    if (isFirstOpen) {
                        this.animateOpen(this.popover);
                    }
                }, delays.openDelay);
            });

            // Close on hover leave from menu item
            li.addEventListener("mouseleave", (e) => {
                clearTimeout(li.openTimeout);
                isHoveringMenu = false;

                if (this.withinMenuChrome(e.relatedTarget)) {
                    return;
                }

                // Start close timeout when leaving menu item
                pendingCloseTimeout = setTimeout(() => {
                    this.closePopover();
                }, delays.closeDelay);
            });

            // Track active state
            trigger.parentElement.addEventListener("mouseenter", () => {
                trigger.parentElement.classList.add("is-active");
            });

            trigger.parentElement.addEventListener("mouseleave", () => {
                trigger.parentElement.classList.remove("is-active");
            });
        });
    }
}

document.addEventListener("DOMContentLoaded", () => new MegaMenu());
